import React, { useEffect, useState } from 'react';
import { css } from '@emotion/css';
import ApplicationStatisticsContainer from '../../../state/containers/ApplicationStatisticsContainer';
import AnalyticsInterval from '../../../models/analytics/AnalyticsInterval';
import ApplicationTopMissesTable from './ApplicationTopMissesTable';
import FeatureContainer from '../../../state/containers/FeatureContainer';
import ApplicationTopHitsTable from './ApplicationTopHitsTable';
import AnalyticsSelectField from '../../../components/analytics/AnalyticsSelectField';
import AnalyticsRequestFilter from '../../../models/analytics/AnalyticsRequestFilter';
import ApplicationOrderingExportWrapper from './ApplicationOrderingExportWrapper';
import SsmlContainer from '../../../state/containers/SsmlContainer';
import ApplicationEnvironmentsContainer from '../../../state/containers/ApplicationEnvironmentsContainer';
import UserApplicationPreferencesContainer from '../../../state/containers/UserApplicationPreferencesContainer';
import ApplicationModel from '../../../models/applications/api/ApplicationModel';
import { filter } from 'lodash';
import ApplicationOrderingAnalyticsReport from './ApplicationOrderingAnalyticsReport';

interface ApplicationTopHitsTableProps {
    stateContainer: ApplicationStatisticsContainer
    featureContainer: FeatureContainer
    interval: AnalyticsInterval
    innerInterval: AnalyticsInterval
    filter: AnalyticsRequestFilter
    applicationId: string
    ssmlContainer: SsmlContainer
    envContainer: ApplicationEnvironmentsContainer
    userApplicationPreferencesContainer: UserApplicationPreferencesContainer
    history: any
    application: ApplicationModel
}

export interface OrderingStatisticsPreferences {
    applicationId: string
    callerNumbersToIgnore: string[]
    assistantNumbersToUse: string[]
}

const ApplicationStatistics = (props: ApplicationTopHitsTableProps) => {
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadChartData();
    }, [props.interval, props.filter])

    const loadChartData = async () => {
        if (loading) return;
        await setLoading(true);
        const { applicationId, filter } = props;
        const promises = [];
        promises.push(props.stateContainer.loadTopContentHitsForApp(applicationId, filter));
        promises.push(props.stateContainer.loadTopContentMissesForApp(applicationId, filter));
        promises.push(props.stateContainer.loadMissStats(applicationId, filter));
        promises.push(props.stateContainer.loadStats(applicationId, filter));
        promises.push(props.featureContainer.getSystemFeatures());
        await Promise.all(promises);
        await setLoading(false);
    }
    const handleDeltaPeriodChange = async (option: string) => {
        const { applicationId, filter, stateContainer } = props;
        await stateContainer.handleDeltaPeriodChange(option);
        stateContainer.loadTopContentHitsForApp(applicationId, filter);
        stateContainer.loadTopContentMissesForApp(applicationId, filter);
        stateContainer.loadMissStats(applicationId, filter);
        stateContainer.loadStats(applicationId, filter);
    }
    const previousDates = props.stateContainer.getPreviousDates(props.filter.startDate, props.filter.endDate);
    const buildCacheKey = props.stateContainer.buildCacheKey;
    const nonFoodOrderingDisplayOptions = [...props.stateContainer.displayOptions].filter(d => !d.includes('AI Ordering'));
    return (<div className={wrapperStyle}>
        <div className="options-row-container">
            {
                props.application.featureFlags?.find(f => f.name === 'Food Ordering') ?
                    <AnalyticsSelectField label="Display:" options={props.stateContainer.displayOptions.map(s => ({ label: s, value: s }))} value={props.stateContainer.state.currentDisplayOption} onChange={(e) => props.stateContainer.handleDisplayOptionChange(e.value)} />
                    :
                    <AnalyticsSelectField label="Display:" options={nonFoodOrderingDisplayOptions.map(s => ({ label: s, value: s }))} value={props.stateContainer.state.currentDisplayOption} onChange={(e) => props.stateContainer.handleDisplayOptionChange(e.value)} />
            }
            <div className="end-container">
                <AnalyticsSelectField label="Compare to:" options={['Previous Period', 'Same Time Last Year'].map(s => ({ label: s, value: s }))} value={props.stateContainer.state.currentDeltaPeriod} onChange={(e) => handleDeltaPeriodChange(e.value)} />
                <AnalyticsSelectField label="Change in:" options={['Value', 'Percentage', 'Value & Percentage'].map(s => ({ label: s, value: s }))} value={props.stateContainer.state.currentDeltaType} onChange={(e) => props.stateContainer.handleDeltaTypeChange(e.value)} />
            </div>
        </div>
        {props.stateContainer.state.currentDisplayOption == 'Content Hits' &&
            <ApplicationTopHitsTable isLoading={loading}
                data={props.stateContainer.state.appTopHits.find(a => buildCacheKey(a.applicationId, a.filter) == buildCacheKey(props.applicationId, props.filter))}
                totals={props.stateContainer.state.appStats.find(a => buildCacheKey(a.id, a.filter) == buildCacheKey(props.applicationId, props.filter))?.data}
                previousTotals={props.stateContainer.state.appStats.find(a => buildCacheKey(a.id, a.filter) == buildCacheKey(props.applicationId, { ...props.filter, startDate: previousDates.startDate, endDate: previousDates.endDate }))?.data}
                featureContainer={props.featureContainer}
                startDate={props.filter.startDate}
                endDate={props.filter.endDate}
                showPercentage={props.stateContainer.state.currentDeltaType.toLocaleLowerCase().indexOf('percentage') > -1}
                showValue={props.stateContainer.state.currentDeltaType.toLocaleLowerCase().indexOf('value') > -1} />}
        {props.stateContainer.state.currentDisplayOption == 'Content Misses' &&
            <ApplicationTopMissesTable isLoading={loading}
                data={props.stateContainer.state.appTopMisses.find(a => buildCacheKey(a.applicationId, a.filter) == buildCacheKey(props.applicationId, props.filter))}
                totals={props.stateContainer.state.appMissStats.find(a => buildCacheKey(a.id, a.filter) == buildCacheKey(props.applicationId, props.filter))}
                previousTotals={props.stateContainer.state.appMissStats.find(a => buildCacheKey(a.id, a.filter) == buildCacheKey(props.applicationId, { ...props.filter, startDate: previousDates.startDate, endDate: previousDates.endDate }))}
                featureContainer={props.featureContainer}
                startDate={props.filter.startDate}
                endDate={props.filter.endDate}
                showPercentage={props.stateContainer.state.currentDeltaType.toLocaleLowerCase().indexOf('percentage') > -1}
                showValue={props.stateContainer.state.currentDeltaType.toLocaleLowerCase().indexOf('value') > -1} />}
        {props.stateContainer.state.currentDisplayOption === 'AI Ordering' &&
            <ApplicationOrderingAnalyticsReport
                filter={props.filter}
                applicationId={props.applicationId}
            />
        }
        {props.stateContainer.state.currentDisplayOption === 'AI Ordering Export' &&
            <ApplicationOrderingExportWrapper
                {...props}
                filter={props.filter}
                interval={props.interval}
                innerInterval={props.innerInterval}
                ssmlContainer={props.ssmlContainer}
                envContainer={props.envContainer}
                history={props.history}
                userApplicationPreferencesContainer={props.userApplicationPreferencesContainer}
                applicationId={props.applicationId}
            />
        }
    </div>)

}
const wrapperStyle = css`
    .card-container {
        display: flex;
    }
    .options-row-container {
        display: flex;
        align-items: center;
        margin: 32px;
    }
    .end-container {
        display: flex;
        align-items: center;
        margin-right: 0;
        margin-left: auto;
        >div {
            margin-left: 24px;
        }
    }
`;

export default ApplicationStatistics;